/**
 * LimitUpgradePrompt Component
 * 
 * Modal shown when a free tier user tries to create an entity
 * but has reached their limit.
 */

import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Button, Icon, GlassPanel } from './index'
import './LimitUpgradePrompt.css'

// Entity type display names
const ENTITY_LABELS = {
  projects: 'projects',
  characters: 'characters',
  locations: 'locations',
  events: 'events',
  shots: 'shots',
  images: 'images',
}

// Icons for each entity type
const ENTITY_ICONS = {
  projects: 'projects',
  characters: 'characters',
  locations: 'locations',
  events: 'timeline',
  shots: 'storyboard',
  images: 'images',
}

/**
 * LimitUpgradePrompt Component
 * 
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Close handler
 * @param {string} entityType - Type of entity that hit the limit
 * @param {number} limit - The current limit value
 */
export function LimitUpgradePrompt({
  isOpen,
  onClose,
  entityType = 'characters',
  limit = 5,
}) {
  const label = ENTITY_LABELS[entityType] || entityType
  const icon = ENTITY_ICONS[entityType] || 'info'

  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  }

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 20 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 25,
      },
    },
    exit: { opacity: 0, scale: 0.95, y: 20 },
  }

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="limit-upgrade-prompt__backdrop"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={onClose}
        >
          <motion.div
            className="limit-upgrade-prompt"
            variants={modalVariants}
            onClick={(e) => e.stopPropagation()}
          >
            <GlassPanel glow="amber" padding="lg" className="limit-upgrade-prompt__panel">
              {/* Close button */}
              <button className="limit-upgrade-prompt__close" onClick={onClose}>
                <Icon name="close" size={20} />
              </button>

              {/* Icon */}
              <div className="limit-upgrade-prompt__icon">
                <Icon name={icon} size={32} />
              </div>

              {/* Title */}
              <h2 className="limit-upgrade-prompt__title">
                {label.charAt(0).toUpperCase() + label.slice(1)} Limit Reached
              </h2>

              {/* Message */}
              <p className="limit-upgrade-prompt__message">
                You've reached the free tier limit of <strong>{limit} {label}</strong>.
                {entityType === 'projects' 
                  ? ' Upgrade to create unlimited projects and unlock team collaboration.'
                  : ' Upgrade to create unlimited entities and unlock the full creative toolkit.'
                }
              </p>

              {/* Features list */}
              <ul className="limit-upgrade-prompt__features">
                <li>
                  <Icon name="check" size={16} />
                  <span>Unlimited {label}</span>
                </li>
                <li>
                  <Icon name="check" size={16} />
                  <span>Team collaboration</span>
                </li>
                <li>
                  <Icon name="check" size={16} />
                  <span>AI-powered generation tools</span>
                </li>
                <li>
                  <Icon name="check" size={16} />
                  <span>Priority support</span>
                </li>
              </ul>

              {/* Actions */}
              <div className="limit-upgrade-prompt__actions">
                <Button variant="outline" onClick={onClose}>
                  Maybe Later
                </Button>
                <Link to="/payment" onClick={onClose}>
                  <Button variant="primary">
                    View Plans
                  </Button>
                </Link>
              </div>

              {/* Hint */}
              <p className="limit-upgrade-prompt__hint">
                No credit card required to explore plans
              </p>
            </GlassPanel>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )

  return createPortal(modalContent, document.body)
}

export default LimitUpgradePrompt
